export const supportedLanguages = [
    'de',
    'en',
    'it',
    'pl',
    'es',
    'fr',
    'nl',
    'cs',
    'sl',
    'hu'
] as const;

export type SupportedLanguage = (typeof supportedLanguages)[number];

export function normalizeLocale(locale?: string): SupportedLanguage {
    const trimmed = locale?.trim();
    if (!trimmed) {
        return 'en';
    }

    const normalized = trimmed.toLowerCase().replaceAll('_', '-');
    const [baseLanguage] = normalized.split('-');

    if (supportedLanguages.includes(baseLanguage as SupportedLanguage)) {
        return baseLanguage as SupportedLanguage;
    }

    return 'en';
}

export type Locale = SupportedLanguage | `${SupportedLanguage}-${string}`;

export function normalizeToFullLocale(locale?: string): string {
    const trimmed = locale?.trim();
    if (!trimmed) {
        return 'en-US';
    }

    try {
        const loc = new Intl.Locale(trimmed.replaceAll('_', '-'));
        if (loc.region) {
            return `${loc.language}-${loc.region}`;
        }
        const maximized = loc.maximize();
        return `${maximized.language}-${maximized.region}`;
    } catch {
        return 'en-US';
    }
}

export interface I18nMessages {
    [key: string]: string;
}

export interface I18nConfig {
    locale: string;
    messages?: Partial<I18nMessages>;
    fallbackLocale?: string;
    devMode?: boolean;
}

class I18nStore {
    private messages: I18nMessages = {};
    private locale: SupportedLanguage;
    private fallbackLocale: SupportedLanguage;
    private overrides: Partial<I18nMessages> = {};
    private devMode: boolean;

    constructor(config: I18nConfig) {
        this.locale = normalizeLocale(config.locale);
        this.fallbackLocale = normalizeLocale(config.fallbackLocale);
        this.overrides = config.messages || {};
        this.devMode = config.devMode || false;
    }

    public async loadMessages(signal?: AbortSignal): Promise<void> {
        try {
            const response = await fetch(
                `https://intl.sportalliance.com/universal-payment-component/${this.locale}.json`,
                { signal }
            );
            if (!response.ok) {
                throw new Error(
                    `Failed to fetch translations for locale: ${this.locale}`
                );
            }
            this.messages = (await response.json()) as I18nMessages;
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                return;
            }
            console.warn(
                `Failed to load translations for ${this.locale}, using fallback:`,
                error
            );
            if (this.locale !== this.fallbackLocale) {
                try {
                    const fallbackResponse = await fetch(
                        `https://intl.sportalliance.com/universal-payment-component/${this.fallbackLocale}.json`,
                        { signal }
                    );
                    if (fallbackResponse.ok) {
                        this.messages =
                            (await fallbackResponse.json()) as I18nMessages;
                    }
                } catch (fallbackError) {
                    if (
                        fallbackError instanceof DOMException &&
                        fallbackError.name === 'AbortError'
                    ) {
                        return;
                    }
                    console.warn(
                        `Failed to load fallback translations for ${this.fallbackLocale}:`,
                        fallbackError
                    );
                }
            }
        }
    }

    public t(key: string, fallback?: string, params?: string[]): string {
        if (this.devMode) {
            return key;
        }

        let message: string;

        if (key in this.overrides) {
            message = this.overrides[key] as string;
        } else if (this.messages[key]) {
            message = this.messages[key];
        } else {
            message = fallback || key;
        }

        if (params) {
            params.forEach((value, index) => {
                message = message.replace(
                    new RegExp(`\\{${index}\\}`, 'g'),
                    value
                );
            });
        }

        return message;
    }

    public isHidden(key: string): boolean {
        return key in this.overrides && this.overrides[key] === '';
    }

    public setOverrides(overrides: Partial<I18nMessages>): void {
        this.overrides = { ...this.overrides, ...overrides };
    }

    public getLocale(): SupportedLanguage {
        return this.locale;
    }

    public setLocale(locale: string): void {
        this.locale = normalizeLocale(locale);
    }
}

export default I18nStore;
